import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Modal,
  Button,
  Upload,
  Input,
  Checkbox,
  Select,
  Row,
  Col,
  Card,
  Progress,
  Tag,
  Space,
  Typography,
  message as antMessage,
} from 'antd';
import {
  UploadOutlined,
  PlayCircleOutlined,
  StopOutlined,
  ClearOutlined,
} from '@ant-design/icons';
import { useAppDispatch } from 'store';
import { createTemplate } from 'store/slices/push.slice';
import { translateText, SUPPORTED_LANGUAGES } from 'utils/translation';

const { TextArea } = Input;
const { Text } = Typography;

type JsonData = Record<string, Record<string, string[]>>;

type JsonFormat = 'days' | 'events';

type LogType = 'ok' | 'error' | 'info' | 'warn' | 'dim' | 'head';

interface LogEntry {
  id: number;
  text: string;
  type: LogType;
}

const DAY_ORDER = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

const DAY_NAMES: Record<string, string> = {
  monday:    'Понедельник',
  tuesday:   'Вторник',
  wednesday: 'Среда',
  thursday:  'Четверг',
  friday:    'Пятница',
  saturday:  'Суббота',
  sunday:    'Воскресенье',
};

const EVENT_NAMES: Record<string, string> = {
  install:      '📲 Install',
  registration: '📝 Registration',
  dep:          '💰 Deposit',
  first_dep:    '💵 First Deposit',
  signup:       '✍️ Sign Up',
  ftd:          '🏆 FTD',
};

const LOG_COLORS: Record<LogType, string> = {
  ok:    '#56d364',
  error: '#f85149',
  info:  '#58a6ff',
  warn:  '#e3b341',
  dim:   '#8888aa',
  head:  '#a78bfa',
};

const HR = '─'.repeat(42);

const sleep = (ms: number) => new Promise<void>(r => setTimeout(r, ms));

const parseDelayToMinutes = (key: string): number => {
  const m = key.match(/^(\d+)\s*(min|hour|h|day|d)s?$/i);
  if (!m) return 0;
  const n = parseInt(m[1], 10);
  const unit = m[2].toLowerCase();
  if (unit === 'min') return n;
  if (unit === 'hour' || unit === 'h') return n * 60;
  if (unit === 'day' || unit === 'd') return n * 1440;
  return 0;
};

const sortSlotKeys = (keys: string[], format: JsonFormat): string[] => {
  if (format === 'days') return [...keys].sort();
  return [...keys].sort((a, b) => parseDelayToMinutes(a) - parseDelayToMinutes(b));
};

const detectFormat = (data: JsonData): JsonFormat => {
  const keys = Object.keys(data).map(k => k.toLowerCase());
  const hasDays = keys.some(k => DAY_ORDER.includes(k));
  return hasDays ? 'days' : 'events';
};

interface BulkCreateModalProps {
  open: boolean;
  onClose: () => void;
  appId: number;
  onComplete?: () => void;
}

const BulkCreateModal: React.FC<BulkCreateModalProps> = ({
  open,
  onClose,
  appId,
  onComplete,
}) => {
  const dispatch = useAppDispatch();
  const [jsonText, setJsonText]     = useState('');
  const [jsonData, setJsonData]     = useState<JsonData | null>(null);
  const [jsonFormat, setJsonFormat] = useState<JsonFormat>('days');
  const [slotChecked, setSlotChecked] = useState<Record<string, Record<string, boolean>>>({});
  const [selectedLangs, setSelectedLangs] = useState<Record<string, boolean>>(
    Object.fromEntries(SUPPORTED_LANGUAGES.map(l => [l.code, true]))
  );
  const [sourceLangCode, setSourceLangCode] = useState('en');
  const [delayMs, setDelayMs]               = useState(500);
  const [isRunning, setIsRunning]   = useState(false);
  const [progress, setProgress]     = useState(0);
  const [statusText, setStatusText] = useState('Готов к работе');
  const [logs, setLogs]             = useState<LogEntry[]>([]);
  const logIdRef  = useRef(0);
  const stopRef   = useRef(false);
  const logEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [logs]);

  const addLog = useCallback((text: string, type: LogType = 'dim') => {
    setLogs(prev => [...prev, { id: ++logIdRef.current, text, type }]);
  }, []);
  const getGroupOrder = (data: JsonData, format: JsonFormat): string[] => {
    if (format === 'days') {
      return DAY_ORDER.filter(d => d in data);
    }
    return Object.keys(data);
  };

  const getGroupLabel = (group: string): string => {
    if (DAY_NAMES[group]) return DAY_NAMES[group];
    if (EVENT_NAMES[group.toLowerCase()]) return EVENT_NAMES[group.toLowerCase()];
    return group.charAt(0).toUpperCase() + group.slice(1);
  };

  const getFormatTag = (): string => {
    return jsonFormat === 'days' ? '📅 Дни недели' : '⚡ События';
  };
  const applyJsonData = (text: string) => {
    try {
      const data = JSON.parse(text) as JsonData;
      const format = detectFormat(data);
      const groups = getGroupOrder(data, format);

      if (!groups.length) {
        throw new Error('Не найдено ни одной группы (дни или события)');
      }

      setJsonData(data);
      setJsonFormat(format);

      const initial: Record<string, Record<string, boolean>> = {};
      for (const group of groups) {
        initial[group] = {};
        const keys = sortSlotKeys(Object.keys(data[group]), format);
        for (const t of keys) {
          initial[group][t] = true;
        }
      }
      setSlotChecked(initial);

      const totalSlots = groups.reduce((acc, g) => acc + Object.keys(data[g]).length, 0);
      const formatLabel = format === 'days' ? 'дни недели' : 'события';
      addLog(`✅ JSON загружен (формат: ${formatLabel})`, 'ok');
      addLog(`   Группы: ${groups.join(', ')}`, 'dim');
      addLog(`   Всего слотов: ${totalSlots}`, 'dim');
    } catch (e: any) {
      addLog(`❌ Ошибка разбора JSON: ${e.message}`, 'error');
      antMessage.error('Ошибка разбора JSON: ' + e.message);
    }
  };

  const handleJsonParse = () => {
    if (!jsonText.trim()) { antMessage.warning('Вставьте JSON данные'); return; }
    setJsonData(null);
    setSlotChecked({});
    applyJsonData(jsonText.trim());
  };

  const handleFileUpload = (file: File) => {
    const reader = new FileReader();
    reader.onload = e => {
      const text = e.target?.result as string;
      setJsonText(text);
      setJsonData(null);
      setSlotChecked({});
      applyJsonData(text);
    };
    reader.readAsText(file, 'utf-8');
    return false;
  };
  const isGroupAllChecked    = (group: string) => slotChecked[group] && Object.values(slotChecked[group]).every(Boolean);
  const isGroupIndeterminate = (group: string) => {
    if (!slotChecked[group]) return false;
    const vals = Object.values(slotChecked[group]);
    return vals.some(Boolean) && !vals.every(Boolean);
  };

  const toggleGroupAll = (group: string, checked: boolean) =>
    setSlotChecked(prev => ({
      ...prev,
      [group]: Object.fromEntries(Object.keys(prev[group]).map(t => [t, checked])),
    }));

  const toggleSlot = (group: string, slot: string, checked: boolean) =>
    setSlotChecked(prev => ({
      ...prev,
      [group]: { ...prev[group], [slot]: checked },
    }));

  const selectAllSlots = (checked: boolean) =>
    setSlotChecked(prev => {
      const next = { ...prev };
      for (const group of Object.keys(next)) {
        next[group] = Object.fromEntries(Object.keys(next[group]).map(t => [t, checked]));
      }
      return next;
    });
  const selectedSlotCount = Object.values(slotChecked).reduce(
    (acc, slots) => acc + Object.values(slots).filter(Boolean).length, 0
  );
  const selectedLangCount = Object.values(selectedLangs).filter(Boolean).length;
  const totalTemplates    = selectedSlotCount * selectedLangCount;
  const handlePreview = () => {
    if (!jsonData) { antMessage.warning('Сначала загрузите JSON'); return; }

    const langAbbrs = SUPPORTED_LANGUAGES
      .filter(l => selectedLangs[l.code])
      .map(l => l.abbr);

    addLog(`\n${HR}`, 'head');
    addLog(' ПРЕДПРОСМОТР', 'head');
    addLog(HR, 'head');
    addLog(`  Формат: ${jsonFormat === 'days' ? 'Дни недели' : 'События'}`, 'info');
    addLog(`  Шаблонов к созданию: ${totalTemplates}`, 'info');
    addLog(`  Языки: ${langAbbrs.join(', ')}`, 'info');
    addLog('', 'dim');

    const groups = jsonFormat === 'days'
      ? DAY_ORDER.filter(d => slotChecked[d])
      : Object.keys(slotChecked);

    for (const group of groups) {
      if (!slotChecked[group]) continue;
      const slots = Object.entries(slotChecked[group])
        .filter(([, v]) => v)
        .map(([t]) => t);
      if (!slots.length) continue;
      const label = getGroupLabel(group);
      addLog(`  ${label.padEnd(18)} ${slots.join(', ')}`, 'dim');
    }
    addLog(`${HR}\n`, 'head');
  };

  const handleStart = async () => {
    if (!jsonData)          { antMessage.warning('Сначала загрузите и разберите JSON'); return; }
    if (!selectedSlotCount) { antMessage.warning('Выберите хотя бы один тайм-слот');   return; }
    if (!selectedLangCount) { antMessage.warning('Выберите хотя бы один язык');         return; }

    type Task = {
      group: string; slot: string;
      titleSrc: string; bodySrc: string;
      langCode: string; langAbbr: string;
    };

    const groups = jsonFormat === 'days'
      ? DAY_ORDER.filter(d => slotChecked[d])
      : Object.keys(slotChecked);

    const tasks: Task[] = [];
    for (const group of groups) {
      if (!slotChecked[group]) continue;
      const sortedSlots = sortSlotKeys(Object.keys(slotChecked[group]), jsonFormat);
      for (const slot of sortedSlots) {
        if (!slotChecked[group][slot]) continue;
        const content = jsonData[group]?.[slot];
        if (!content || content.length < 2) {
          addLog(`⚠ ${group}/${slot}: нужно 2 элемента [title, body]`, 'warn');
          continue;
        }
        const [titleSrc, bodySrc] = content;
        for (const lang of SUPPORTED_LANGUAGES) {
          if (!selectedLangs[lang.code]) continue;
          tasks.push({ group, slot, titleSrc, bodySrc, langCode: lang.code, langAbbr: lang.abbr });
        }
      }
    }

    if (!tasks.length) { antMessage.warning('Нет задач для выполнения'); return; }

    setIsRunning(true);
    stopRef.current = false;
    setProgress(0);

    addLog(`\n${HR}`, 'head');
    addLog(`🚀 Начинаем: ${tasks.length} шаблонов`, 'head');
    addLog(`   App ID: ${appId}  |  Delay: ${delayMs}ms  |  Формат: ${jsonFormat}`, 'dim');
    addLog(HR, 'head');

    let ok = 0, err = 0, skip = 0;

    for (let i = 0; i < tasks.length; i++) {
      if (stopRef.current) {
        addLog('⏹ Остановлено пользователем', 'warn');
        break;
      }

      const { group, slot, titleSrc, bodySrc, langCode, langAbbr } = tasks[i];
      const groupLabel   = getGroupLabel(group);
      const templateName = `${groupLabel} - ${slot} (${langAbbr})`;
      setProgress(Math.round(((i + 1) / tasks.length) * 100));
      setStatusText(`${i + 1}/${tasks.length}  ${templateName}`);
      const opts = { from: sourceLangCode, to: langCode, timeout: 8_000 };
      const [{ text: title, translated: tOk1 }, { text: body, translated: tOk2 }] =
        await Promise.all([
          translateText(titleSrc, opts),
          translateText(bodySrc, opts),
        ]);

      if (!tOk1 || !tOk2) {
        addLog(`⚠ [${i + 1}/${tasks.length}] Перевод ${langAbbr}: использован оригинал`, 'warn');
      }
      try {
        await dispatch(createTemplate({
          app_id:      appId,
          name:        templateName,
          description: '',
          title,
          body,
        })).unwrap();

        ok++;
        addLog(`✅ [${i + 1}/${tasks.length}] ${templateName}`, 'ok');
      } catch (e: any) {
        const msg = (e?.message || String(e)).toLowerCase();
        const isConflict =
          msg.includes('409') ||
          msg.includes('already exist') ||
          msg.includes('duplicate');

        if (isConflict) {
          skip++;
          addLog(`⚡ [${i + 1}/${tasks.length}] ${templateName} — уже существует`, 'warn');
        } else {
          err++;
          addLog(`❌ [${i + 1}/${tasks.length}] ${templateName}: ${(e?.message || '').slice(0, 120)}`, 'error');
        }
      }

      if (delayMs > 0 && i < tasks.length - 1) {
        await sleep(delayMs);
      }
    }

    addLog(`\n${HR}`, 'head');
    addLog(`  ✅ Успешно:   ${ok}`, 'ok');
    if (skip) addLog(`  ⚡ Пропущено: ${skip}`, 'warn');
    if (err)  addLog(`  ❌ Ошибок:    ${err}`, 'error');
    addLog(`${HR}\n`, 'head');

    setStatusText(`Готово: ${ok} создано${skip ? `, ${skip} пропущено` : ''}${err ? `, ${err} ошибок` : ''}`);
    setProgress(100);
    setIsRunning(false);

    if (ok > 0) onComplete?.();
  };

  const handleStop = () => { stopRef.current = true; };

  const handleReset = () => {
    if (isRunning) return;
    setJsonText('');
    setJsonData(null);
    setSlotChecked({});
    setProgress(0);
    setStatusText('Готов к работе');
    setLogs([]);
  };

  const handleClose = () => {
    if (isRunning) stopRef.current = true;
    onClose();
  };

  const groupsPresent = jsonFormat === 'days'
    ? DAY_ORDER.filter(d => slotChecked[d])
    : Object.keys(slotChecked);

  return (
    <Modal
      title="🔔 Массовое создание шаблонов"
      open={open}
      onCancel={handleClose}
      width={1080}
      centered
      footer={null}
      destroyOnClose={false}
      styles={{ body: { padding: '16px 20px' } }}
    >
      <Row gutter={14}>
        <Col span={14}>
          <Space direction="vertical" size={10} style={{ width: '100%' }}>
            <Card
              size="small"
              title="📂 JSON данные"
              extra={
                jsonData
                  ? <Space size={4}><Tag color="success">Загружен</Tag><Tag>{getFormatTag()}</Tag></Space>
                  : <Tag color="default">Не загружен</Tag>
              }
            >
              <TextArea
                value={jsonText}
                onChange={e => {
                  setJsonText(e.target.value);
                  if (jsonData) { setJsonData(null); setSlotChecked({}); }
                }}
                placeholder={
                  `Формат 1 — дни недели:\n{\n  "monday": { "14:00": ["Title", "Body"] }\n}\n\nФормат 2 — события:\n{\n  "install": { "10 min": ["Title", "Body"] },\n  "registration": { "1 hour": ["Title", "Body"] }\n}`
                }
                rows={5}
                style={{ fontFamily: 'Consolas, monospace', fontSize: 12, marginBottom: 8 }}
                disabled={isRunning}
              />
              <Space size={6}>
                <Button size="small" onClick={handleJsonParse} disabled={!jsonText.trim() || isRunning}>
                  Разобрать JSON
                </Button>
                <Upload accept=".json" showUploadList={false} beforeUpload={handleFileUpload}>
                  <Button size="small" icon={<UploadOutlined />} disabled={isRunning}>
                    Загрузить файл
                  </Button>
                </Upload>
                <Button size="small" onClick={handleReset} disabled={isRunning}>
                  Сбросить
                </Button>
              </Space>
            </Card>
            {groupsPresent.length > 0 && (
              <Card
                size="small"
                title={
                  <Space size={6}>
                    <span>{jsonFormat === 'days' ? '📅 Расписание' : '⚡ События'}</span>
                    <Tag color="blue">{selectedSlotCount} слотов</Tag>
                  </Space>
                }
                extra={
                  <Space size={4}>
                    <Button size="small" onClick={() => selectAllSlots(true)}  disabled={isRunning}>Все</Button>
                    <Button size="small" onClick={() => selectAllSlots(false)} disabled={isRunning}>Снять</Button>
                  </Space>
                }
              >
                <Space direction="vertical" size={5} style={{ width: '100%' }}>
                  {groupsPresent.map(group => (
                    <div
                      key={group}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        background: '#fafafa',
                        borderRadius: 6,
                        padding: '5px 10px',
                        border: '1px solid #f0f0f0',
                        flexWrap: 'wrap',
                      }}
                    >
                      <Checkbox
                        checked={isGroupAllChecked(group)}
                        indeterminate={isGroupIndeterminate(group)}
                        onChange={e => toggleGroupAll(group, e.target.checked)}
                        disabled={isRunning}
                        style={{ fontWeight: 600, minWidth: 140 }}
                      >
                        {getGroupLabel(group)}
                      </Checkbox>
                      <div style={{ width: 1, height: 14, background: '#e0e0e0', flexShrink: 0 }} />
                      {sortSlotKeys(Object.keys(slotChecked[group]), jsonFormat).map(slot => (
                        <Checkbox
                          key={slot}
                          checked={slotChecked[group][slot]}
                          onChange={e => toggleSlot(group, slot, e.target.checked)}
                          disabled={isRunning}
                          style={{ fontFamily: 'Consolas, monospace', fontSize: 12, color: '#1677ff' }}
                        >
                          {slot}
                        </Checkbox>
                      ))}
                    </div>
                  ))}
                </Space>
              </Card>
            )}
            <Card
              size="small"
              title={
                <Space size={6}>
                  <span>🌍 Языки вывода</span>
                  <Tag color="purple">{selectedLangCount} выбрано</Tag>
                </Space>
              }
              extra={
                <Space size={4}>
                  <Button
                    size="small"
                    disabled={isRunning}
                    onClick={() =>
                      setSelectedLangs(Object.fromEntries(SUPPORTED_LANGUAGES.map(l => [l.code, true])))
                    }
                  >
                    Все
                  </Button>
                  <Button
                    size="small"
                    disabled={isRunning}
                    onClick={() =>
                      setSelectedLangs(Object.fromEntries(SUPPORTED_LANGUAGES.map(l => [l.code, false])))
                    }
                  >
                    Снять
                  </Button>
                </Space>
              }
            >
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '4px 0' }}>
                {SUPPORTED_LANGUAGES.map(lang => (
                  <Checkbox
                    key={lang.code}
                    checked={selectedLangs[lang.code]}
                    onChange={e =>
                      setSelectedLangs(prev => ({ ...prev, [lang.code]: e.target.checked }))
                    }
                    disabled={isRunning}
                  >
                    <Text style={{ fontSize: 12 }}>
                      <Text code style={{ fontSize: 11 }}>{lang.abbr}</Text> {lang.name}
                    </Text>
                  </Checkbox>
                ))}
              </div>
            </Card>

          </Space>
        </Col>
        <Col span={10}>
          <Space direction="vertical" size={10} style={{ width: '100%' }}>
            <Card size="small" title="⚙️ Настройки">
              <Space direction="vertical" size={8} style={{ width: '100%' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Text style={{ width: 110, flexShrink: 0, fontSize: 13 }}>Язык JSON:</Text>
                  <Select
                    value={sourceLangCode}
                    onChange={setSourceLangCode}
                    disabled={isRunning}
                    style={{ flex: 1 }}
                    size="small"
                    options={SUPPORTED_LANGUAGES.map(l => ({
                      label: `${l.abbr} — ${l.name}`,
                      value: l.code,
                    }))}
                  />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Text style={{ width: 110, flexShrink: 0, fontSize: 13 }}>Задержка (мс):</Text>
                  <Input
                    type="number"
                    value={delayMs}
                    onChange={e => setDelayMs(Math.max(0, Number(e.target.value)))}
                    disabled={isRunning}
                    size="small"
                    style={{ width: 80 }}
                    min={0}
                    max={10000}
                  />
                  <Text type="secondary" style={{ fontSize: 12 }}>между запросами</Text>
                </div>
              </Space>
            </Card>
            <Card size="small">
              <Space direction="vertical" size={8} style={{ width: '100%' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>{statusText}</Text>
                  {totalTemplates > 0 && <Tag color="blue">{totalTemplates} шаблонов</Tag>}
                </div>
                <Progress
                  percent={progress}
                  size="small"
                  status={isRunning ? 'active' : progress === 100 ? 'success' : 'normal'}
                />
                <Space wrap size={6}>
                  <Button size="small" onClick={handlePreview} disabled={!jsonData || isRunning}>
                    👁 Предпросмотр
                  </Button>
                  <Button
                    type="primary"
                    size="small"
                    icon={<PlayCircleOutlined />}
                    onClick={handleStart}
                    disabled={isRunning || !jsonData || !totalTemplates}
                    loading={isRunning}
                  >
                    {isRunning ? 'Создаём...' : 'Создать'}
                  </Button>
                  <Button
                    danger
                    size="small"
                    icon={<StopOutlined />}
                    onClick={handleStop}
                    disabled={!isRunning}
                  >
                    Стоп
                  </Button>
                  <Button size="small" icon={<ClearOutlined />} onClick={() => setLogs([])} disabled={isRunning}>
                    Лог
                  </Button>
                </Space>
              </Space>
            </Card>
            <div
              style={{
                background: '#0d1117',
                borderRadius: 8,
                padding: '10px 12px',
                height: 388,
                overflowY: 'auto',
                fontFamily: 'Consolas, "Courier New", monospace',
                fontSize: 12,
                lineHeight: 1.65,
                border: '1px solid #21262d',
              }}
            >
              {logs.length === 0 ? (
                <span style={{ color: '#8888aa' }}>Лог выполнения появится здесь...</span>
              ) : (
                logs.map(entry => (
                  <div key={entry.id} style={{ color: LOG_COLORS[entry.type], whiteSpace: 'pre-wrap' }}>
                    {entry.text}
                  </div>
                ))
              )}
              <div ref={logEndRef} />
            </div>

          </Space>
        </Col>
      </Row>
    </Modal>
  );
};

export default BulkCreateModal;